import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { nanoid } from "@reduxjs/toolkit";
import { Nullable } from "../../../../types/nullable";
import {
  CustomerOrganization,
  CustomerOrgCompany,
} from "../../../../types/apiTypes";
import { NameIdOption } from "../../../../types/commonTypes";
import {
  customerOrgAndCompAutocomplete,
  getCustomerOrganizationsById,
} from "../../../../../features/customer/customerAPI";
import { debounce } from "@mui/material";
import { AutocompleteType } from "../../../../types/autoComplete";

type SharedReturn = {
  customerOptions: CustomerOrgCompany[];
  customerCompanyOptions: AutocompleteType[];
  onInputChange: (value: string, reason: string) => void;
  isLoading: boolean;
  /** True when `customerOptions` holds the full, fixed set for one company. */
  isFixedOptions: boolean;
};

type SingleReturn = SharedReturn & {
  customerOrganization: Nullable<CustomerOrganization>;
  onChange: (value: Nullable<CustomerOrganization>) => void;
};

type MultiReturn = SharedReturn & {
  customerOrganizations: CustomerOrganization[];
  onChange: (value: CustomerOrganization[] | null) => void;
};

/**
 * Hook for autocompleting a customer organization, optionally scoped to a
 * single customer company.
 *
 * - Without `customerCompany`: searches across all companies/organizations
 *   via `customerOrgAndCompAutocomplete` as the user types (debounced).
 * - With `customerCompany`: all organizations of that company are fetched
 *   once via `getCustomerOrganizationsById` (`isFixedOptions` is true), and
 *   no further requests are made while typing - the autocomplete component
 *   filters the fixed list itself, so results can never leak in
 *   organizations from other companies.
 */
export function useCustomerOrgCompanyAutocomplete(options: {
  multiple: true;
  customerCompany?: NameIdOption;
}): MultiReturn;
export function useCustomerOrgCompanyAutocomplete(options?: {
  multiple?: false;
  customerCompany?: NameIdOption;
}): SingleReturn;
export function useCustomerOrgCompanyAutocomplete(options?: {
  multiple?: boolean;
  customerCompany?: NameIdOption;
}): SingleReturn | MultiReturn {
  const isMultiple = options?.multiple ?? false;
  const { customerCompany } = options ?? {};
  const customerCompanyId = customerCompany?.id;
  const customerCompanyName = customerCompany?.name;

  const [customerOrganization, setCustomerOrganization] =
    useState<Nullable<CustomerOrganization>>(null);
  const [customerOrganizations, setCustomerOrganizations] = useState<
    CustomerOrganization[]
  >([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOptions, setCustomerOptions] = useState<CustomerOrgCompany[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestIdRef = useRef<string | null>(null);

  const customerCompanyOptions = useMemo(() => {
    if (!customerOptions.length) return [];
    return customerOptions.flatMap((item) => ({
      id: item.id,
      name: item.name,
    }));
  }, [customerOptions]);

  // Fixed mode: fetch all organizations of the company once.
  useEffect(() => {
    if (!customerCompanyId || !customerCompanyName) return;
    const requestId = nanoid();
    latestRequestIdRef.current = requestId;
    setIsLoading(true);
    getCustomerOrganizationsById(
      { customerCompanyId },
      { requestId },
    )
      .then((orgs) => {
        if (latestRequestIdRef.current !== requestId) return;
        setCustomerOptions([
          {
            id: customerCompanyId,
            name: customerCompanyName,
            customerOrganizations: orgs,
          },
        ]);
      })
      .finally(() => {
        if (latestRequestIdRef.current === requestId) setIsLoading(false);
      });
  }, [customerCompanyId, customerCompanyName]);

  // Unscoped search mode: only used without a fixed company.
  useEffect(() => {
    if (customerCompanyId) return;
    if (!customerSearch) {
      setCustomerOptions([]);
      return;
    }
    const requestId = nanoid();
    latestRequestIdRef.current = requestId;
    setIsLoading(true);
    customerOrgAndCompAutocomplete({ name: customerSearch })
      .then((res: CustomerOrgCompany[]) => {
        if (latestRequestIdRef.current !== requestId) return;
        setCustomerOptions(res);
      })
      .finally(() => {
        if (latestRequestIdRef.current === requestId) setIsLoading(false);
      });
  }, [customerSearch, customerCompanyId]);

  const onInputChangeCallback = useCallback((value: string, reason: string) => {
    if (reason === "input") {
      setCustomerSearch(value);
    }
  }, []);

  const debouncedOnInputChange = useMemo(
    () => debounce(onInputChangeCallback, 400),
    [onInputChangeCallback],
  );

  const onInputChange = customerCompanyId ? () => {} : debouncedOnInputChange;

  const onChangeSingle = useCallback(
    (value: Nullable<CustomerOrganization>) => {
      setCustomerOrganization(value);
    },
    [],
  );

  const onChangeMulti = useCallback((value: CustomerOrganization[] | null) => {
    setCustomerOrganizations(value ?? []);
  }, []);

  const shared: SharedReturn = {
    customerOptions,
    customerCompanyOptions,
    onInputChange,
    isLoading,
    isFixedOptions: !!customerCompanyId,
  };

  if (isMultiple) {
    return {
      ...shared,
      customerOrganizations,
      onChange: onChangeMulti,
    };
  }

  return {
    ...shared,
    customerOrganization,
    onChange: onChangeSingle,
  };
}

export default useCustomerOrgCompanyAutocomplete;
